import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormField, Heading, Image, List, Select, Text, TextInput } from "grommet";
import { debounce } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import { AliasViewModel, searchAliases } from "../aliases/AliasStore";
import { NavigationButton } from "../components/NavigationButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import TeamDetails from "./TeamDetails";
import TeamMemberRole from "./TeamMemberRole";
import {
    createTeamMember,
    CreateTeamMemberRequest,
    getTeam,
    getTeamMembers,
    removeTeamMember,
    RemoveTeamMemberRequest,
    TeamMembershipViewModel,
    TeamViewModel,
    updateTeam,
    UpdateTeamRequest,
} from "./TeamsStore";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;

const TeamProfile = () => {
    let { id } = useParams<{ id: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    React.useEffect(() => {
        if (pathname == `/teams/${id}`) {
            navigate(`/teams/${id}/members`, { replace: true });
        }
    }, [pathname]);

    const [members, setMembers] = React.useState([] as TeamMembershipViewModel[]);
    const [team, setTeam] = React.useState({} as TeamViewModel | UpdateTeamRequest);
    const [isLoading, setIsLoading] = React.useState(true);

    let [error, setError] = React.useState("");

    const fetchTeamMembers = () => {
        return getTeamMembers(id).then((result) => {
            if (result.status == 200) {
                setMembers(result.data.result);
            } else {
                setMembers([]);
            }
        });
    };

    const fetchTeam = () => {
        return getTeam(id).then((result) => {
            if (result.status == 200) {
                setTeam(result.data.result);
            } else {
                setTeam({} as TeamViewModel);
            }
        });
    };

    React.useEffect(() => {
        Promise.all([fetchTeamMembers(), fetchTeam()]).then(() => {
            setIsLoading(false);
        });
    }, []);

    const [isUpdating, setIsUpdating] = React.useState(false);

    const [showAddMemberModal, setShowAddMemberModal] = React.useState(false);

    const onChange = (updatedTeam) => {
        setTeam({ ...team, ...updatedTeam });
    };

    let update = () => {
        setIsUpdating(true);
        updateTeam(team).then(
            (result) => {
                fetchTeam().then((team) => {
                    setIsUpdating(false);
                });
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };
    let [userSearchResult, setUserSearchResult] = React.useState([] as Array<AliasViewModel>);

    const search = (filter, organizationID) => {
        searchAliases(filter, organizationID, 0, 10).then((response) => {
            setUserSearchResult(response);
        });
    };

    const debounceSearch = React.useMemo(() => debounce(search, 300), []);

    let [userFilter, setUserFilter] = React.useState("");
    const [role, setRole] = React.useState("Member");

    const [memberCreateError, setMemeberCreateError] = React.useState("");

    React.useEffect(() => {
        debounceSearch(userFilter, team.organizationID);
    }, [userFilter, team.organizationID]);

    const addMember = (alias: AliasViewModel) => {
        setMemeberCreateError("");

        createTeamMember({ aliasID: alias.id, teamID: team.id, roleName: role } as CreateTeamMemberRequest).then((response) => {
            if (response.success) {
                //setShowAddMemberModal(false);
                fetchTeamMembers();
            } else {
                setMemeberCreateError(response.errors.join("/n"));
            }
        });
    };

    const [showRemoveMemberModal, setShowRemoveMemberModal] = React.useState(false);
    const [memberToRemove, setMemberToRemove] = React.useState({} as AliasViewModel);

    const startRemoveMember = (member: AliasViewModel) => {
        setMemberToRemove(member);
        setShowRemoveMemberModal(true);
    };

    const removeMember = (alias: AliasViewModel) => {
        removeTeamMember({ aliasID: alias.id, teamID: team.id } as RemoveTeamMemberRequest).then((response) => {
            fetchTeamMembers();
            setShowRemoveMemberModal(false);
        });
    };

    const hasWritePermission = usePermission("sport:teams.write");

    return (
        <div>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    <Box direction="row" align="center" gap="small">
                        <Image src={team.logoUri + "?ts=" + Math.random()} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {team.name}
                    </Box>
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium">
                    <NavigationButton href={`/teams/${team.id}/members`} text={"Members"}></NavigationButton>
                    <NavigationButton href={`/teams/${team.id}/details`} text={"Details"}></NavigationButton>
                    <Button href={"/ue/teams/" + team.id} label="Home Page"></Button>
                    {error && <NavigationButton href={`/teams/${team.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>

                <Routes>
                    <Route
                        path="members"
                        element={
                            <>
                                <Box direction="row" gap="small" pad={"small"} wrap>
                                    {members.map((member) => (
                                        <Box key={member.id} align="center" pad={"small"} background={"background-back"} round="18px" width={"180px"} margin={{ top: "small" }}>
                                            <Avatar name={member.alias.name} round={"18px"} size="100" src={member.alias.photoUri}></Avatar>
                                            <Text truncate="tip">{member.alias.name}</Text>
                                            <TeamMemberRole role={member.roleName}></TeamMemberRole>

                                            {hasWritePermission && (
                                                <Box hoverIndicator="background-front" onClick={() => startRemoveMember(member.alias)} style={{ float: "right" }}>
                                                    <FontAwesomeIcon icon={["fad", "circle-xmark"]} color="red"></FontAwesomeIcon>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                    {hasWritePermission && (
                                        <Box
                                            align="center"
                                            pad={"small"}
                                            background={"background-back"}
                                            round="18px"
                                            width={"180px"}
                                            hoverIndicator="background-front"
                                            onClick={() => setShowAddMemberModal(true)}
                                            height={"180px"}
                                            justify="center"
                                            margin={{ top: "small" }}
                                        >
                                            <FontAwesomeIcon icon={["fad", "circle-plus"]}></FontAwesomeIcon>
                                            <Text>Add</Text>
                                        </Box>
                                    )}
                                    <StyledModal isOpen={showAddMemberModal} setIsOpen={setShowAddMemberModal}>
                                        <Box width={"medium"}>
                                            <FormField label="Choose a Role" required>
                                                <Select value={role} onChange={(e) => setRole(e.target.value)} options={["Member", "Captain", "League Master"]} />
                                            </FormField>
                                            <FormField label="Search">
                                                <TextInput size="xs" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
                                            </FormField>
                                            <List
                                                pad="xs"
                                                data={userSearchResult}
                                                itemKey="id"
                                                primaryKey={(member: AliasViewModel) => (
                                                    <Text size="small" key={member.id}>
                                                        <Avatar name={member.name} src={member.photoUri} round size="25px"></Avatar> {member.name}
                                                    </Text>
                                                )}
                                                action={(item, index) => (
                                                    <Button
                                                        hoverIndicator
                                                        key={item.id}
                                                        size="small"
                                                        secondary
                                                        onClick={() => addMember(item)}
                                                        disabled={!role}
                                                        icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>}
                                                    />
                                                )}
                                            ></List>
                                            <Text>
                                                <pre>{memberCreateError}</pre>
                                            </Text>
                                        </Box>
                                    </StyledModal>

                                    <StyledModal isOpen={showRemoveMemberModal} setIsOpen={setShowRemoveMemberModal}>
                                        <Box width={"medium"}>
                                            <Text>Are you sure you want to remove this member?</Text>
                                            <Box direction="row" justify="end" pad={"small"} gap="small">
                                                <Button onClick={() => setShowRemoveMemberModal(false)} label="Cancel"></Button>
                                                <Button primary onClick={() => removeMember(memberToRemove)} label="Remove" color="status-critical"></Button>
                                            </Box>
                                        </Box>
                                    </StyledModal>
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="details"
                        element={
                            <>
                                <TeamDetails team={team} isBusy={!hasWritePermission || isUpdating} onChange={onChange}></TeamDetails>
                                <br />
                                {hasWritePermission && (
                                    <>
                                        {/* @ts-ignore */}
                                        <Button primary onClick={update} disabled={isUpdating || !team.name} label="Update"></Button>
                                    </>
                                )}
                            </>
                        }
                    />
                    <Route
                        path="home"
                        element={
                            <>
                                <Button href={"/ue/teams/" + team.id} icon={idBadge} label="View Home Page"></Button>
                            </>
                        }
                    />
                    <Route
                        path="error"
                        element={
                            <>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>
        </div>
    );
};

export default TeamProfile;
